import * as Sentry from "@sentry/react-native";
import * as React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Platform } from "react-native";

import { useCheckForUpdatesFunction } from "../../../utils/expoUpdates/useCheckForUpdatesFunction";
import type { CheckForUpdatesOutcome } from "../../../utils/expoUpdates/useCheckForUpdatesFunction";
import { OtaUpdateOverlay } from "../components/OtaUpdateOverlay";

type OtaUpdateOverlayContextValue = {
  isVisible: boolean;
  isChecking: boolean;
  message?: string;
  outcome?: CheckForUpdatesOutcome;
  handleSchemaVersionMismatch: () => void;
  retryCheckForUpdates: () => void;
  showApplyingUpdateOverlay: () => void;
};

const OtaUpdateOverlayContext =
  createContext<OtaUpdateOverlayContextValue | null>(null);

type OtaUpdateOverlayProviderProps = {
  children: React.ReactNode;
  /**
   * When false, the overlay UI will not render (useful for tests to avoid animation mocks).
   * Defaults to true.
   */
  renderOverlay?: boolean;
};

export const OtaUpdateOverlayProvider = ({
  children,
  renderOverlay = true,
}: OtaUpdateOverlayProviderProps) => {
  const checkForUpdates = useCheckForUpdatesFunction();
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [outcome, setOutcome] = useState<CheckForUpdatesOutcome | undefined>(
    undefined
  );

  const runCheckWithRetry = useCallback(async () => {
    // Retry up to 5 minutes total before surfacing the last result
    const maxDurationMs = 5 * 60 * 1000;
    const startedAtMs = Date.now();
    let lastOutcome: CheckForUpdatesOutcome | undefined = undefined;
    let lastMessage: string | undefined = undefined;

    setIsChecking(true);
    try {
      // Small backoff that grows slightly to reduce server hammering
      let attempt = 0;
      while (Date.now() - startedAtMs < maxDurationMs) {
        const result = await checkForUpdates();
        lastOutcome = result.outcome;
        lastMessage = result.message;
        setOutcome(result.outcome);
        setMessage(result.message);

        if (result.outcome === "update_applied" || Platform.OS === "web") {
          return; // will reload or unsupported
        }

        // If no update or error, wait and retry until timeout
        const delayMs = Math.min(15000, 2000 + attempt * 1000);
        attempt += 1;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    } finally {
      // After timeout, surface the last known outcome/message
      //IF we got an error -- lets report to Sentry so we can run these down.
      if (lastOutcome === "error" || lastOutcome === "no_update") {
        Sentry.captureException(new Error(`OTA update error: ${lastOutcome}`));
      }
      if (lastOutcome) {
        setOutcome(lastOutcome);
      }
      if (lastMessage) {
        setMessage(lastMessage);
      }
      setIsChecking(false);
    }
  }, [checkForUpdates]);

  const handleSchemaVersionMismatch = useCallback(() => {
    if (hasTriggered || Platform.OS === "web") {
      return;
    }

    setHasTriggered(true);
    setIsVisible(true);
    // Fire-and-forget; overlay will remain visible and show status
    runCheckWithRetry();
  }, [hasTriggered, runCheckWithRetry]);

  const showApplyingUpdateOverlay = useCallback(() => {
    setOutcome(undefined);
    setMessage(undefined);
    setIsChecking(true);
    setIsVisible(true);
  }, []);

  const retryCheckForUpdates = useCallback(() => {
    if (Platform.OS === "web") {
      return;
    }
    runCheckWithRetry();
  }, [runCheckWithRetry]);

  const value = useMemo(
    () => ({
      isVisible,
      isChecking,
      message,
      outcome,
      handleSchemaVersionMismatch,
      retryCheckForUpdates,
      showApplyingUpdateOverlay,
    }),
    [
      handleSchemaVersionMismatch,
      isVisible,
      isChecking,
      message,
      outcome,
      retryCheckForUpdates,
      showApplyingUpdateOverlay,
    ]
  );

  return (
    <OtaUpdateOverlayContext.Provider value={value}>
      {!isVisible || !renderOverlay ? children : <OtaUpdateOverlay />}
    </OtaUpdateOverlayContext.Provider>
  );
};

export const useOtaUpdateOverlayContext = () => {
  const context = useContext(OtaUpdateOverlayContext);

  if (!context) {
    throw new Error(
      "useOtaUpdateOverlayContext must be used within an OtaUpdateOverlayProvider"
    );
  }

  return context;
};
